import { useMemo } from 'react';
import useConfigurationStore from '../store/configurationStore';

/**
 * Renders a questionnaire-style option card.
 * Supports input_type: 'yes_no', 'text', 'dropdown', 'date', 'number'
 * and sub_fields for follow-up questions when selected/answered.
 */
export default function QuestionCard({ option }) {
    const {
        selectedOptions,
        optionNotes,
        toggleOption,
        setOptionNote,
    } = useConfigurationStore();

    const optionId = Number(option.id);
    const isSelected = !!selectedOptions[optionId];
    const notes = optionNotes[optionId] || {};
    const inputType = option.input_type || 'standard';

    const subFields = useMemo(() => {
        if (!option.sub_fields) return [];
        try {
            return typeof option.sub_fields === 'string'
                ? JSON.parse(option.sub_fields)
                : option.sub_fields;
        } catch {
            return [];
        }
    }, [option.sub_fields]);

    const updateNote = (key, value) => {
        setOptionNote(optionId, { ...notes, [key]: value });
    };

    // For yes_no: toggle selects/deselects the option
    const handleYesNo = (val) => {
        if (val && !isSelected) {
            toggleOption(option);
        } else if (!val && isSelected) {
            toggleOption(option);
        }
    };

    // For text/dropdown/date/number: auto-select when user types, store value
    const handleDirectInput = (value) => {
        if (!isSelected && value) {
            toggleOption(option);
        }
        setOptionNote(optionId, { value });
    };

    const renderSubField = (field, idx) => {
        const fieldValue = notes[field.key] || '';
        const fieldKey = `${optionId}-${field.key}`;

        switch (field.type) {
            case 'text':
                return (
                    <div key={fieldKey} className="ast-qcard__subfield">
                        <label className="ast-qcard__sublabel">{field.label}</label>
                        <input
                            type="text"
                            className="ast-input ast-qcard__input"
                            value={fieldValue}
                            onChange={(e) => updateNote(field.key, e.target.value)}
                            placeholder={field.label}
                        />
                    </div>
                );
            case 'number':
                return (
                    <div key={fieldKey} className="ast-qcard__subfield">
                        <label className="ast-qcard__sublabel">{field.label}</label>
                        <input
                            type="number"
                            className="ast-input ast-qcard__input ast-qcard__input--short"
                            value={fieldValue}
                            onChange={(e) => updateNote(field.key, e.target.value)}
                            min={field.min || 0}
                        />
                    </div>
                );
            case 'yes_no':
                return (
                    <div key={fieldKey} className="ast-qcard__subfield">
                        <label className="ast-qcard__sublabel">{field.label}</label>
                        <div className="ast-qcard__toggle-group">
                            <button
                                type="button"
                                className={`ast-qcard__toggle-btn ${fieldValue === true || fieldValue === 'true' ? 'ast-qcard__toggle-btn--active' : ''}`}
                                onClick={() => updateNote(field.key, true)}
                            >Ja</button>
                            <button
                                type="button"
                                className={`ast-qcard__toggle-btn ${fieldValue === false || fieldValue === 'false' ? 'ast-qcard__toggle-btn--active' : ''}`}
                                onClick={() => updateNote(field.key, false)}
                            >Nee</button>
                        </div>
                    </div>
                );
            case 'dropdown':
                return (
                    <div key={fieldKey} className="ast-qcard__subfield">
                        <label className="ast-qcard__sublabel">{field.label}</label>
                        <select
                            className="ast-input ast-qcard__select"
                            value={fieldValue}
                            onChange={(e) => updateNote(field.key, e.target.value)}
                        >
                            <option value="">-- Kies --</option>
                            {(field.options || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                );
            case 'multi_check':
                const checkedValues = Array.isArray(fieldValue) ? fieldValue : [];
                return (
                    <div key={fieldKey} className="ast-qcard__subfield">
                        <label className="ast-qcard__sublabel">{field.label}</label>
                        <div className="ast-qcard__check-group">
                            {(field.options || []).map((opt) => (
                                <label key={opt} className="ast-qcard__check-label">
                                    <input
                                        type="checkbox"
                                        checked={checkedValues.includes(opt)}
                                        onChange={(e) => {
                                            const newVal = e.target.checked
                                                ? [...checkedValues, opt]
                                                : checkedValues.filter((v) => v !== opt);
                                            updateNote(field.key, newVal);
                                        }}
                                    />
                                    {opt}
                                </label>
                            ))}
                        </div>
                    </div>
                );
            case 'date':
                return (
                    <div key={fieldKey} className="ast-qcard__subfield">
                        <label className="ast-qcard__sublabel">{field.label}</label>
                        <input
                            type="date"
                            className="ast-input ast-qcard__input ast-qcard__input--short"
                            value={fieldValue}
                            onChange={(e) => updateNote(field.key, e.target.value)}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    // ============================================================
    // Render based on input_type
    // ============================================================

    if (inputType === 'yes_no') {
        return (
            <div className={`ast-qcard ${isSelected ? 'ast-qcard--yes' : ''}`}>
                <div className="ast-qcard__question">
                    <span className="ast-qcard__label">{option.name}</span>
                    <div className="ast-qcard__toggle-group">
                        <button
                            type="button"
                            className={`ast-qcard__toggle-btn ${isSelected ? 'ast-qcard__toggle-btn--active ast-qcard__toggle-btn--ja' : ''}`}
                            onClick={() => handleYesNo(true)}
                        >Ja</button>
                        <button
                            type="button"
                            className={`ast-qcard__toggle-btn ${!isSelected ? 'ast-qcard__toggle-btn--active ast-qcard__toggle-btn--nee' : ''}`}
                            onClick={() => handleYesNo(false)}
                        >Nee</button>
                    </div>
                </div>
                {option.description && (
                    <p className="ast-qcard__desc">{option.description}</p>
                )}
                {isSelected && subFields.length > 0 && (
                    <div className="ast-qcard__subfields">
                        {subFields.map(renderSubField)}
                    </div>
                )}
            </div>
        );
    }

    if (inputType === 'text') {
        return (
            <div className="ast-qcard ast-qcard--field">
                <label className="ast-qcard__label">{option.name}</label>
                <input
                    type="text"
                    className="ast-input ast-qcard__input"
                    value={notes.value || ''}
                    onChange={(e) => handleDirectInput(e.target.value)}
                    placeholder={option.description || option.name}
                />
            </div>
        );
    }

    if (inputType === 'number') {
        return (
            <div className="ast-qcard ast-qcard--field">
                <label className="ast-qcard__label">{option.name}</label>
                <input
                    type="number"
                    className="ast-input ast-qcard__input ast-qcard__input--short"
                    value={notes.value || ''}
                    onChange={(e) => handleDirectInput(e.target.value)}
                    min="0"
                />
            </div>
        );
    }

    if (inputType === 'dropdown') {
        const dropdownOptions = subFields.length > 0 && subFields[0].options
            ? subFields[0].options
            : [];
        return (
            <div className="ast-qcard ast-qcard--field">
                <label className="ast-qcard__label">{option.name}</label>
                <select
                    className="ast-input ast-qcard__select"
                    value={notes.value || ''}
                    onChange={(e) => handleDirectInput(e.target.value)}
                >
                    <option value="">-- Kies --</option>
                    {dropdownOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                {subFields.length > 1 && notes.value && (
                    <div className="ast-qcard__subfields">
                        {subFields.slice(1).map(renderSubField)}
                    </div>
                )}
            </div>
        );
    }

    if (inputType === 'date') {
        return (
            <div className="ast-qcard ast-qcard--field">
                <label className="ast-qcard__label">{option.name}</label>
                <input
                    type="date"
                    className="ast-input ast-qcard__input ast-qcard__input--short"
                    value={notes.value || ''}
                    onChange={(e) => handleDirectInput(e.target.value)}
                />
            </div>
        );
    }

    // Fallback: standard (shouldn't reach here, OptionCard handles those)
    return null;
}
